import { useId, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { useLayer } from '../../lib/layers';

export interface DrawerProps {
  readonly title: ReactNode;
  /** Plain-text name for screen readers when `title` is not a string. */
  readonly ariaLabel?: string;
  /** Line under the title in fg-2. */
  readonly sub?: ReactNode;
  /** Next to the title, e.g. a StatusChip. */
  readonly titleAdornment?: ReactNode;
  readonly onClose: () => void;
  /** Panel width in px: 560 (screens), 600 (invoice preview), 480 (activity, payment). */
  readonly width?: number;
  /** Rendered between the header and the scrolling body (e.g. Tabs). */
  readonly toolbar?: ReactNode;
  /** Right-aligned footer buttons. */
  readonly footer?: ReactNode;
  /** Body padding/gap override; default `16px 20px` with an 18px gap. */
  readonly bodyClassName?: string;
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  readonly children?: ReactNode;
}

/**
 * Right-hand drawer (design: fixed, top/right/bottom 0, z 40, bg-3, border-left, shadow-lg).
 * Header 18px/600 title with sub and a 32px "×"; scrolling body; footer bar with actions.
 * Esc closes, Tab is trapped inside, focus returns to the opener.
 */
export function Drawer({
  title,
  ariaLabel,
  sub,
  titleAdornment,
  onClose,
  width = 560,
  toolbar,
  footer,
  bodyClassName,
  initialFocusRef,
  children,
}: DrawerProps) {
  const panelRef = useRef<HTMLElement>(null);
  const titleId = useId();
  useLayer({ kind: 'drawer', panelRef, onClose, initialFocusRef, initialFocus: 'panel' });

  return createPortal(
    <aside
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : titleId}
      tabIndex={-1}
      className="fixed inset-y-0 right-0 z-40 flex max-w-full flex-col border-l border-border-1 bg-bg-3 text-[13px] text-fg-1 shadow-lg outline-none"
      style={{ width }}
    >
      <div className="flex items-start gap-3 border-b border-border-1 pb-3.5 pl-5 pr-4 pt-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <span id={titleId} className="truncate text-[18px] font-semibold">
              {title}
            </span>
            {titleAdornment}
          </div>
          {sub !== undefined && <span className="text-fg-2">{sub}</span>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="size-8 flex-none rounded-lg border-0 bg-transparent text-[18px] text-fg-2 hover:bg-neutral-100"
        >
          ×
        </button>
      </div>
      {toolbar}
      <div className={cn('flex flex-1 flex-col gap-[18px] overflow-y-auto px-5 py-4', bodyClassName)}>
        {children}
      </div>
      {footer !== undefined && (
        <div className="flex justify-end gap-2 border-t border-border-1 px-4 py-3">{footer}</div>
      )}
    </aside>,
    document.body,
  );
}
